<template>
  <div ref="wrapperRef">
    <slot />
  </div>
</template>
<script lang="ts">
import { OnboardingTooltipId } from '@src/types/onboardingTooltips.types'
import {
  defineComponent, onMounted, onUnmounted, PropType, watch, useTemplateRef, TemplateRef
} from 'vue'
import { useI18n } from '@src/composables/useI18n'
import _throttle from 'lodash/throttle'
import { useOnboardingTooltips } from '../composables/useOnboardingTooltips'

export default defineComponent({
  name: 'SMOnboardingTooltipWrapper',
  props: {
    onboardingTooltipId: {
      type: String as PropType<OnboardingTooltipId>,
      required: true
    },
    useUpdateInterval: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { slots }) {
    const element: TemplateRef = useTemplateRef('wrapperRef')
    let unwatch: () => void
    let intervalId: ReturnType<typeof setInterval>

    const {
      updateOverlayCutout, currentTooltipId, updateBreakpoint, isHighlighting
    } = useOnboardingTooltips()

    const updateListener = _throttle(() => {
      try {
        requestAnimationFrame(() => {
          if (!element.value) return
          updateOverlayCutout({
            dimensions: (element.value as HTMLElement).getBoundingClientRect(),
            tooltipId: props.onboardingTooltipId
          })
          updateBreakpoint(window.innerWidth)
        })
      } catch (e) {
        // do nothing
      }
    }, 1000)

    onMounted(() => {
      // Watch for tooltip changes
      unwatch = watch(
        currentTooltipId,
        () => {
          updateListener()
        },
        { immediate: true }
      )

      // Listen to window resize
      window.addEventListener('resize', updateListener)

      // Optional interval updates for components that layout slowly
      if (props.useUpdateInterval) {
        intervalId = setInterval(() => {
          if (isHighlighting.value) {
            updateListener()
          }
        }, 1000)
      }
    })

    onUnmounted(() => {
      try {
        updateOverlayCutout({
          dimensions: new DOMRect(),
          tooltipId: props.onboardingTooltipId
        })
      } catch (e) {
        // DO NOTHING
      }

      unwatch?.()
      window.removeEventListener('resize', updateListener)
      if (props.useUpdateInterval) {
        clearInterval(intervalId)
      }
    })

    const { t } = useI18n()

    return {
      slots,
      t
    }
  }
})
</script>
